import { Component, computed, inject, Input, OnChanges, SimpleChanges, signal } from '@angular/core';
import { finalize, forkJoin } from 'rxjs';
import { JournalEntry, JournalEntryStatus, JournalLineItem } from '../../../models/journal-entry/journal-entry';
import { JournalEntryProxyService } from '../../../proxies/journal-entry.proxy.service';
import { EnumService } from '../../../services/enum.service';

/**
 * Shared GL impact summary (#5024) — the condensed view of the journal entries a document produced when it
 * posted. Used by every origin document that posts to the GL (#5022, #5030); the page inventory is in #5027.
 *
 * Deliberately the GL-impact box ONLY: the surrounding card and any page-specific rows (an AP control
 * account, for instance) stay with the host, because they come from the source document rather than the
 * entry and are meaningless on Cash Funding or a Withholding Remittance.
 *
 * Hosts pass plain entry ids. A host whose model holds a different shape normalizes it first — Bank
 * Transaction unwraps its EntityKeyByReference (#5033) — so this component never learns a second shape.
 * The input is a list because a document can produce more than one entry: an AR advance books one on
 * receipt and another when applied to an invoice.
 */
@Component({
  selector: 'app-journal-entry-summary',
  templateUrl: './journal-entry-summary.component.html',
  styleUrl: './journal-entry-summary.component.scss',
})
export class JournalEntrySummaryComponent implements OnChanges {
  private readonly proxy = inject(JournalEntryProxyService);
  private readonly enumService = inject(EnumService);

  /** Currency enum labels, for naming a line's OWN currency (#5144) — the host's `currencyLabel` names the
   *  document's currency, which on a mixed entry is exactly what a foreign line is not in. */
  private readonly currencyLabels: Record<number, string> = this.enumService.getEnum('Currency').values as Record<number, string>;

  /**
   * Journal entry ids to summarize. Accepts a bare id for the common single-entry case. Null and duplicate
   * ids are dropped, so a host can bind an optional reference straight through without guarding first.
   */
  @Input({ required: true }) references: string | readonly (string | null | undefined)[] | null | undefined = null;

  /**
   * Bump this to refetch the SAME ids — for when the entry changed underneath us rather than the document
   * pointing at a different one.
   *
   * <p>Reversing an entry is exactly that case. An AP document being cancelled keeps its
   * <c>JournalEntryReference</c> deliberately (the reversal is stamped beside it, not over it), so after
   * the host reloads it hands us the id it handed us before — and the ids alone can never say that the
   * entry behind them just went from Posted to Reversed. Without this the summary would go on reporting a
   * posting that had been undone.</p>
   *
   * <p>Optional, and inert until it changes: the hosts that show an entry which cannot change under them
   * bind nothing and keep the plain id-comparison behaviour.</p>
   */
  @Input() reloadToken: unknown = undefined;

  /** Currency code shown beside the balanced total, e.g. `USD`. Supplied by the host. */
  @Input() currencyLabel = '';

  /**
   * Optional caption per entry id — `{ '6a95e94b…': 'Send' }`.
   *
   * For a document that posts more than one entry, where the numbers alone do not say which is which: an
   * internal transfer books one entry sending the money and another receiving it, and FI-2026-000050 beside
   * FI-2026-000052 tells the reader nothing. Ids with no caption render as before, so the hosts that pass a
   * single entry need no change.
   *
   * Already-translated strings: the caption is the host's word for its own step, and only the host knows
   * whether its second entry was a receipt or a return.
   */
  @Input() entryLabels: Record<string, string> = {};

  /**
   * Whether `View full entry` can be followed. On by default now that the details page exists (#5026) —
   * every host wants it, so the seven pages carrying this component need no per-page opt-in. A host that
   * renders an entry the user should not be able to open can still turn it off, and the link renders
   * disabled rather than disappearing.
   *
   * <p>It opens in a new tab. The host document stays exactly as the reader left it — mid-draft, mid-scroll
   * — and the details page, which knows only how to go back to the Journal Entries dashboard, never has to
   * guess where the reader came from.</p>
   */
  @Input() canOpenFullEntry = true;

  readonly entries = signal<JournalEntry[]>([]);
  readonly isLoading = signal(0);
  readonly hasEntries = computed(() => this.entries().length > 0);

  private loadedIds: string[] = [];

  labelFor(entry: JournalEntry): string {
    return (entry.id && this.entryLabels[entry.id]) || '';
  }

  /** An entry with no id came from somewhere that cannot be linked to, so the button stays inert. */
  canOpen(entry: JournalEntry): boolean {
    return this.canOpenFullEntry && !!entry.id;
  }

  /**
   * Balanced in the FUNCTIONAL currency — the only currency every line shares (#5144). A per-line-rate
   * entry debits Lempiras and credits dollars in one posting; comparing its raw transaction totals is
   * comparing currencies, and declared every such entry "unbalanced" while the GL had it balanced to the
   * cent. Entries from before functional amounts existed carry zeros there and fall back to the
   * transaction compare that was always right for them. Mirrors journal-entry-details, deliberately.
   */
  isBalanced(entry: JournalEntry): boolean {
    const functionalDebits = entry.totalFunctionalDebits ?? 0;
    const functionalCredits = entry.totalFunctionalCredits ?? 0;
    if (functionalDebits > 0 || functionalCredits > 0) return Math.abs(functionalDebits - functionalCredits) < 0.005;
    return entry.totalDebits === entry.totalCredits;
  }

  /** True when the lines do not all share the entry's currency (#5144) — the transaction totals are then
   *  sums across currencies, so the footer totals functionally instead. */
  isMixedCurrency(entry: JournalEntry): boolean {
    return (entry.lineItems ?? []).some(
      (line) => (line.currency != null && line.currency !== entry.currency) || this.isFunctionalOnly(line),
    );
  }

  /** A line with no transaction amounts at all — the realized exchange difference, which exists only in
   *  the books' currency (#5144). Rendered from its functional figures, or it renders as nothing. */
  isFunctionalOnly(line: JournalLineItem): boolean {
    return !line.debitAmount && !line.creditAmount && (!!line.functionalDebitAmount || !!line.functionalCreditAmount);
  }

  /** The line's own currency label, only when it differs from the entry's. */
  lineCurrencyText(entry: JournalEntry, line: JournalLineItem): string {
    if (line.currency == null || line.currency === entry.currency) return '';
    return this.currencyLabels[line.currency] ?? '';
  }

  /** The books' currency label, from the entry itself rather than the host — the host names its document's
   *  currency, which is not what a functional figure is stated in. */
  functionalCurrencyText(entry: JournalEntry): string {
    return this.currencyLabels[entry.functionalCurrency ?? 0] ?? '';
  }

  /**
   * A line's rate the way a person quotes one: units of the line's currency per 1 unit of the books'
   * currency — `25.8400 HNL / USD`, the number that was read off the wire confirmation and typed in.
   *
   * <p><b>The stored figure is the other way up.</b> `line.exchangeRate` is the multiplier the GL posts
   * with, functional per line-unit, and for Lempiras that is 0.038700 — arithmetically correct, and not a
   * number anybody recognises or can check against a bank advice. Everywhere else in this system a rate is
   * quoted on the USD pivot (`ApDocument.RateToUsd`, a PO's `ExchangeRateToUSD`,
   * `Finance:ExchangeRateBounds`), so showing the reciprocal here made this the one screen speaking a
   * different dialect.</p>
   *
   * <p>Inverted rather than read off the source document, because the entry is the only thing this
   * component is given — and the inversion is exact in both directions, so it stays right for a company
   * whose books are not in dollars (a USD line under HNL books reads `0.038700 USD / HNL`, which is again
   * the quote a person would say).</p>
   */
  lineRateText(entry: JournalEntry, line: JournalLineItem): string {
    const rate = line.exchangeRate ?? 0;
    const lineCurrency = this.effectiveCurrencyText(entry, line);
    if (rate <= 0 || !lineCurrency) return '';

    const quoted = 1 / rate;
    // Six places matches how the rate is captured; trailing zeroes are trimmed so the ordinary 25.84 reads
    // as 25.84 rather than 25.840000.
    return `${Number(quoted.toFixed(6))} ${lineCurrency} / ${this.functionalCurrencyText(entry)}`;
  }

  /** The currency a line's transaction amount is actually stated in — its own when it carries one, the
   *  entry's otherwise. */
  private effectiveCurrency(entry: JournalEntry, line: JournalLineItem): number {
    return line.currency ?? entry.currency ?? 0;
  }

  /** That currency's label, always — unlike {@link lineCurrencyText}, which stays silent when the line
   *  agrees with its entry. Used where the amount is being labelled rather than flagged as unusual. */
  effectiveCurrencyText(entry: JournalEntry, line: JournalLineItem): string {
    return this.currencyLabels[this.effectiveCurrency(entry, line)] ?? '';
  }

  /**
   * True when this line moved money in something other than the books' currency — so the books' figure
   * leads and what actually moved is stated beneath it.
   *
   * <p>Asked against the FUNCTIONAL currency rather than the entry's. An all-Lempira entry under dollar
   * books has no line that disagrees with its entry, and still needs converting for a reader who works in
   * dollars; comparing against the entry would have left exactly that case unconverted.</p>
   *
   * <p>False for entries written before functional amounts existed (#4549): they carry none, so there is
   * nothing to lead with and the transaction figure stays the only figure.</p>
   */
  showsConversion(entry: JournalEntry, line: JournalLineItem): boolean {
    if (this.isFunctionalOnly(line)) return false;
    if (this.effectiveCurrency(entry, line) === (entry.functionalCurrency ?? 0)) return false;
    return !!line.functionalDebitAmount || !!line.functionalCreditAmount;
  }

  /** Whether the footer states its total in the books' currency. True for a mixed entry, whose transaction
   *  total is a sum across currencies, and for any foreign entry, whose total is simply not in dollars. */
  totalsInFunctional(entry: JournalEntry): boolean {
    if (!(entry.totalFunctionalDebits || entry.totalFunctionalCredits)) return false;
    return this.isMixedCurrency(entry) || (entry.currency ?? 0) !== (entry.functionalCurrency ?? 0);
  }

  statusSeverity(status: JournalEntryStatus): 'success' | 'warn' | 'danger' | 'info' {
    switch (status) {
      case JournalEntryStatus.Posted:
        return 'success';
      case JournalEntryStatus.Draft:
        return 'warn';
      case JournalEntryStatus.Reversed:
        return 'danger';
      default:
        return 'info';
    }
  }

  statusLabel(status: JournalEntryStatus): string {
    return JournalEntryStatus[status] ?? '';
  }

  /**
   * Both inputs are settled here rather than in per-input setters, so a pass that changes the ids AND the
   * reload token decides once and fetches once. Angular gives no ordering guarantee between two setters,
   * which is the other reason: whichever ran second would have had to guess what the first had already done.
   */
  ngOnChanges(changes: SimpleChanges): void {
    const ids = this.normalizedIds();
    const idsChanged = !this.sameAsLoaded(ids);
    // Not on the first change: the initial binding is the ids arriving, which loads on its own account.
    const reloadAsked = !!changes['reloadToken'] && !changes['reloadToken'].firstChange;
    if (!idsChanged && !reloadAsked) return;
    this.loadedIds = ids;
    this.load(ids);
  }

  private normalizedIds(): string[] {
    const value = this.references;
    const ids = (typeof value === 'string' ? [value] : (value ?? [])).filter((id): id is string => !!id);
    return [...new Set(ids)];
  }

  private sameAsLoaded(ids: string[]): boolean {
    return ids.length === this.loadedIds.length && ids.every((id, i) => id === this.loadedIds[i]);
  }

  private load(ids: string[]): void {
    if (!ids.length) {
      this.entries.set([]);
      return;
    }
    this.startLoading();
    forkJoin(ids.map((id) => this.proxy.getById(id)))
      .pipe(finalize(() => this.stopLoading()))
      .subscribe({
        // Keep the requested order — an AR advance's receipt entry should sit above its application entry.
        next: (responses) => this.entries.set(responses.map((r) => r.data).filter((e): e is JournalEntry => !!e)),
        // A missing or unreadable entry hides the section rather than breaking the host page: the summary is
        // supplementary, and the document it sits on is still perfectly usable without it.
        error: () => this.entries.set([]),
      });
  }

  private startLoading(): void {
    this.isLoading.update((count) => count + 1);
  }

  private stopLoading(): void {
    this.isLoading.update((count) => Math.max(0, count - 1));
  }
}
